import { AsyncPipe } from '@angular/common';
import { Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { filter, map } from 'rxjs';

import { PrivaContentHeaderComponent } from '@priva/components/content-header';
import { PrivaContentTemplateComponent } from '@priva/components/content-template';
import { PrivaLocalizationModule } from '@priva/localization';

import { selectAppModeIfDefined } from 'app/features/settings/state/settings/settings.selectors';
import { SigninFormComponent } from 'app/shared/components';
import { Credentials, PersonalUserPreferences, UserInfo } from 'app/shared/models';
import { loginSuccess } from 'app/state/user-session/user-session.actions';
import { selectIsAuthenticated } from 'app/state/user-session/user-session.selectors';

@Component({
    selector: 'app-user-session-signin',
    templateUrl: './user-session-signin.component.html',
    styleUrl: './user-session-signin.component.scss',
    imports: [
        PrivaContentTemplateComponent,
        PrivaContentHeaderComponent,
        SigninFormComponent,
        PrivaLocalizationModule,
        AsyncPipe,
    ],
})
// This component is responsible for handling user sign-in and redirecting authenticated users.
// Specifically it is used for Personal Mode
export class UserSessionSigninComponent {
    private destroyRef = inject(DestroyRef);
    private store = inject(Store);
    router = inject(Router);

    requiredRoles$ = this.store.pipe(
        selectAppModeIfDefined,
        map((appMode) => (appMode === 'personal' ? [] : ['supervisor'])),
    );

    title$ = this.store.pipe(
        selectAppModeIfDefined,
        map((appMode) => (appMode === 'personal' ? 'LOGIN.TITLE.DEFAULT' : 'LOGIN.TITLE.SIGN_IN_SUPERVISOR')),
    );

    constructor() {
        this.store
            .pipe(
                select(selectIsAuthenticated),
                filter((isAuthenticated) => !!isAuthenticated),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe(() => {
                this.router.navigateByUrl('/').then();
            });
    }

    onAuthorized({
        credentials,
        userInfo,
        personalUserPreferences,
    }: {
        credentials: Credentials;
        userInfo: UserInfo | undefined;
        personalUserPreferences: PersonalUserPreferences;
    }) {
        const userSession = {
            credentials: { ...credentials },
            data: personalUserPreferences,
            startedOn: Date.now(),
            userInfo,
        };
        this.store.dispatch(loginSuccess({ userSession }));
    }
}
